import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Button from 'components/atoms/Button';
import DropdownField from 'components/molecules/DropdownField';
import { translateText } from '../../utils';
import { Field } from '../controls';
import css from './AdvancedSettings.css';
import { admonSourceListPropType, sitePropType } from './propTypes';

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const normalizeAdmonSourceOption = source => ({
    ...source,
    name: source?.name || '',
    display_name: source?.display_name || source?.name || '',
});

const getAdmonSourceByName = (sources, sourceName) => sources.find(source => source.name === sourceName);

const getCurrentAdmonSourceOption = ({ site, admonSources, adminMode, translate }) => {
    const normalizedSources = admonSources.map(normalizeAdmonSourceOption);
    const currentSourceName =
        (typeof site.admon_revenue_source === 'object' ? site.admon_revenue_source?.name : site.admon_revenue_source) ||
        '';

    if (site.admon_revenue_upcoming_source) {
        const matchedSource = getAdmonSourceByName(normalizedSources, currentSourceName);
        const sourceOption =
            matchedSource ||
            normalizeAdmonSourceOption(
                site.admon_revenue_source || {
                    name: currentSourceName || site.admon_revenue_upcoming_source.admon_source,
                }
            );

        return {
            ...sourceOption,
            display_name: translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_SOURCE_MIGRATING', {
                admonSource: site.admon_revenue_upcoming_source.admon_source,
                startDate: site.admon_revenue_upcoming_source.start_date,
            }),
            readOnly: true,
        };
    }

    if (currentSourceName) {
        const matchedSource = getAdmonSourceByName(normalizedSources, currentSourceName);
        return matchedSource || normalizeAdmonSourceOption(site.admon_revenue_source || { name: currentSourceName });
    }

    if (!normalizedSources.length) {
        return {
            name: '',
            display_name: adminMode
                ? translateText(
                      translate,
                      'STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_SOURCE_NONE_CONTACT_SUPPORT'
                  )
                : translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_SOURCE_NONE'),
            readOnly: adminMode,
        };
    }

    if (!site.admon_revenue_sources?.length) {
        return {
            name: '',
            display_name: translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_SOURCE_NONE'),
        };
    }

    if (site.admon_revenue_sources.length === 1) {
        const matchedSource = getAdmonSourceByName(normalizedSources, site.admon_revenue_sources[0]);
        const sourceOption = matchedSource || {
            name: site.admon_revenue_sources[0],
            display_name: site.admon_revenue_sources[0],
        };

        return sourceOption;
    }

    return {
        name: '',
        display_name: translateText(
            translate,
            'STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_SOURCE_MULTIPLE_CONTACT_SUPPORT'
        ),
        readOnly: true,
    };
};

const AdMonSection = ({
    site,
    appId,
    appName,
    admonConfig,
    adminMode,
    readOnly,
    translate,
    onSitePatch,
    onTrackAppsEvent,
}) => {
    const configuredAdmonSources = useMemo(
        () => admonConfig.admon_revenue_sources_list?.filter(x => x.configured) || [],
        [admonConfig.admon_revenue_sources_list]
    );
    const admonSources = useMemo(
        () => (adminMode ? admonConfig.admon_revenue_sources_list || [] : configuredAdmonSources),
        [adminMode, admonConfig.admon_revenue_sources_list, configuredAdmonSources]
    );
    const currentAdmonSourceOption = getCurrentAdmonSourceOption({
        site,
        admonSources: configuredAdmonSources,
        adminMode,
        translate,
    });
    const currentAdmonSource = currentAdmonSourceOption.name;
    const newAdmonSource =
        typeof site.new_admon_revenue_source === 'object'
            ? site.new_admon_revenue_source?.name
            : site.new_admon_revenue_source || '';
    const admonOptions = useMemo(() => {
        const normalizedSources = admonSources.map(normalizeAdmonSourceOption);

        if (!normalizedSources.some(source => source.name === currentAdmonSourceOption.name)) {
            return [currentAdmonSourceOption, ...normalizedSources];
        }

        if (currentAdmonSourceOption.display_name) {
            return normalizedSources.map(source =>
                source.name === currentAdmonSourceOption.name ? currentAdmonSourceOption : source
            );
        }

        return normalizedSources;
    }, [admonSources, currentAdmonSourceOption]);
    const admonDropdownOptions = useMemo(
        () =>
            admonOptions.map(source => ({
                ...source,
                display_name: <span>{source.display_name}</span>,
            })),
        [admonOptions]
    );
    const renderRichText = (id, data = null) => translate(id, data, { renderInnerHtml: true });

    return (
        <section className={css.formSection}>
            <h4>
                <Translate id="STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION" />
            </h4>
            <div className={css.richText}>
                {renderRichText('STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_REVENUE_SOURCE_EXPLANATION_TITLE')}
            </div>
            <div className={css.richText}>
                {renderRichText('STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_REVENUE_SOURCE_EXPLANATION_SUBTITLE')}
            </div>
            <div className={css.admonSourceRow}>
                <DropdownField
                    label="STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_REVENUE_SOURCE"
                    labelTooltip="STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_REVENUE_SOURCE_TOOLTIP"
                    selected={currentAdmonSource}
                    items={admonDropdownOptions}
                    disabled={
                        readOnly ||
                        site.admon_revenue_source_read_only ||
                        !!currentAdmonSourceOption.readOnly ||
                        !!site.new_admon_dialog_open
                    }
                    onChange={selectedName => {
                        if (selectedName !== currentAdmonSource) {
                            onTrackAppsEvent('admon revenue source edit', {
                                app_id: appId,
                                app_name: appName,
                                site_id: site.id,
                                old_value: currentAdmonSource,
                                new_value: selectedName,
                            });
                        }
                        onSitePatch({ admon_revenue_source: selectedName, admon_changed: true });
                    }}
                    containerClass={classNames(css.admonDropdownField, css.inlineDropdownField)}
                />
                {adminMode && (
                    <Button
                        type="primary"
                        level="level2"
                        disabled={
                            readOnly ||
                            site.new_admon_dialog_open ||
                            site.admon_revenue_source_read_only ||
                            !!currentAdmonSourceOption.readOnly
                        }
                        onClick={() =>
                            onSitePatch({
                                new_admon_dialog_open: true,
                                new_admon_revenue_start_date: site.new_admon_revenue_start_date || getTodayDate(),
                            })
                        }
                    >
                        +
                    </Button>
                )}
            </div>
            {adminMode && (
                <div className={css.richText}>
                    {renderRichText('STATIC.PAGES.APPS_CONFIGURATION.ADD_NEW_AD_MONETIZATION_REVENUE_SOURCE')}
                </div>
            )}
            {adminMode && site.new_admon_dialog_open && (
                <div className={css.newAdmonSourceRow}>
                    <DropdownField
                        label="STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_REVENUE_SOURCE"
                        selected={newAdmonSource}
                        items={admonDropdownOptions}
                        onChange={selectedName =>
                            onSitePatch({
                                new_admon_revenue_source: selectedName,
                                new_admon_revenue_start_date: site.new_admon_revenue_start_date || getTodayDate(),
                            })
                        }
                        containerClass={css.admonDropdownField}
                    />
                    <Field
                        label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.RULE_START_DATE" />}
                        type="date"
                        value={site.new_admon_revenue_start_date || getTodayDate()}
                        onChange={value => onSitePatch({ new_admon_revenue_start_date: value })}
                    />
                    <Button
                        type="secondary"
                        level="level2"
                        onClick={() => onSitePatch({ new_admon_dialog_open: false })}
                    >
                        ×
                    </Button>
                </div>
            )}
            <div className={css.noteLine}>
                <Translate id="STATIC.PAGES.APPS_CONFIGURATION.AD_MONETIZATION_WARNING_LABEL" />
            </div>
        </section>
    );
};

AdMonSection.propTypes = {
    admonConfig: PropTypes.shape({
        admon_revenue_sources_list: admonSourceListPropType,
    }).isRequired,
    adminMode: PropTypes.bool.isRequired,
    appId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    appName: PropTypes.string.isRequired,
    onSitePatch: PropTypes.func.isRequired,
    onTrackAppsEvent: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    translate: PropTypes.func.isRequired,
};

export default AdMonSection;
